/**
 * @fileoverview Admin page for approving or rejecting pending user signups.
 * Only accessible to users listed in the ADMIN_EMAILS environment variable.
 */

import { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../../services/api';
import styles from './UserApprovalPage.module.css';

/**
 * UserApprovalPage Component
 *
 * Admin page for approving or rejecting pending user signups.
 *
 * @param {Object} props
 * @param {Function} props.onBack - Navigate back to budget tracker
 */
function UserApprovalPage({ onBack }) {
    const [pendingUsers, setPendingUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionInProgress, setActionInProgress] = useState(null);

    const loadPendingUsers = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);
            const data = await adminAPI.getPendingUsers();
            setPendingUsers(data.users || []);
        } catch (err) {
            console.error('Error loading pending users:', err);
            setError('Failed to load pending users. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadPendingUsers();
    }, [loadPendingUsers]);

    const handleApprove = async (userKey) => {
        if (actionInProgress) return;

        try {
            setActionInProgress(userKey);
            await adminAPI.approveUser(userKey);
            // Remove user from list
            setPendingUsers(prev => prev.filter(u => u.userKey !== userKey));
        } catch (err) {
            console.error('Error approving user:', err);
            setError('Failed to approve user. Please try again.');
        } finally {
            setActionInProgress(null);
        }
    };

    const handleReject = async (userKey) => {
        if (actionInProgress) return;

        const confirmed = window.confirm(
            'Are you sure you want to reject this user? They will not be able to access the application.'
        );
        if (!confirmed) return;

        try {
            setActionInProgress(userKey);
            await adminAPI.rejectUser(userKey);
            // Remove user from list
            setPendingUsers(prev => prev.filter(u => u.userKey !== userKey));
        } catch (err) {
            console.error('Error rejecting user:', err);
            setError('Failed to reject user. Please try again.');
        } finally {
            setActionInProgress(null);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <button onClick={onBack} className={styles.backButton}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 12H5M12 19l-7-7 7-7" />
                    </svg>
                    Back
                </button>
                <h1 className={styles.title}>User Approval</h1>
                <button onClick={loadPendingUsers} className={styles.refreshButton} disabled={isLoading}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M23 4v6h-6M1 20v-6h6" />
                        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                    </svg>
                </button>
            </div>

            {error && (
                <div className={styles.error}>
                    <span>{error}</span>
                    <button onClick={() => setError(null)} className={styles.dismissError}>
                        Dismiss
                    </button>
                </div>
            )}

            {isLoading ? (
                <div className={styles.loading}>
                    <div className={styles.spinner}></div>
                    <span>Loading pending users...</span>
                </div>
            ) : pendingUsers.length === 0 ? (
                <div className={styles.emptyState}>
                    <div className={styles.emptyIcon}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                            <polyline points="22 4 12 14.01 9 11.01" />
                        </svg>
                    </div>
                    <h2 className={styles.emptyTitle}>All Caught Up</h2>
                    <p className={styles.emptyMessage}>
                        No users are waiting for approval.
                    </p>
                </div>
            ) : (
                <div className={styles.userList}>
                    <div className={styles.listHeader}>
                        <span className={styles.countBadge}>{pendingUsers.length}</span>
                        <span>Pending Approval{pendingUsers.length !== 1 ? 's' : ''}</span>
                    </div>

                    {pendingUsers.map(user => (
                        <div key={user.userKey} className={styles.userCard}>
                            <div className={styles.userInfo}>
                                <div className={styles.userAvatar}>
                                    {(user.fullName || user.email).charAt(0).toUpperCase()}
                                </div>
                                <div className={styles.userDetails}>
                                    <div className={styles.userName}>
                                        {user.fullName || 'No name provided'}
                                    </div>
                                    <div className={styles.userEmail}>{user.email}</div>
                                    <div className={styles.userMeta}>
                                        Signed up: {formatDate(user.createdAt)}
                                    </div>
                                </div>
                            </div>
                            <div className={styles.userActions}>
                                <button
                                    onClick={() => handleApprove(user.userKey)}
                                    className={styles.approveButton}
                                    disabled={actionInProgress === user.userKey}
                                >
                                    {actionInProgress === user.userKey ? 'Processing...' : 'Approve'}
                                </button>
                                <button
                                    onClick={() => handleReject(user.userKey)}
                                    className={styles.rejectButton}
                                    disabled={actionInProgress === user.userKey}
                                >
                                    Reject
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default UserApprovalPage;
